// Plagiarism Pro service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (plagiarism_posting_params)
//   views      app/views/job_postings/steps/plagiarism/_one.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (plagiarism__one)
//   cost       app/models/job_postings/plagiarism_posting.rb (#cost)
//   parity     tasks/post-a-project/parity/enhance-text.md §5
//
// One step, NO AI consent, and NO niche field (the model hardcodes niche
// 'Plagiarism Pro' on the order; the view never renders one). subtitle and
// reference_style are REQUIRED; reference_style_other is required when "Other".
// The requirements file is REQUIRED and accepts .doc/.docx
// (VALID_REQUIREMENT_EDITING_FILE_TYPES now includes application/msword —
// Plumbline Phase 0, ruling C1).

import { REFERENCE_STYLES, RATES } from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

export const plagiarism = {
  key: "plagiarism",
  postingKey: "plagiarism_posting",
  name: "Plagiarism Pro",

  steps: () => [
    // Step 1 — Document details (plagiarism__one).
    {
      id: "details",
      title: "Document details",
      sub: "A deep plagiarism scan and report by a trained editor.",
      fields: [
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        {
          type: "wordcount",
          key: "word_count",
          label: "Word count",
          help: "Minimum 1,000, in increments of 500.",
          required: true,
          min: 1000,
          step: 500,
        },
        {
          // Design option order (MLA, APA, Chicago, Other) — membership is
          // validated, order is not (step_validations.rb:798-799), so the design's
          // order is restored (no `.sort()`).
          type: "select",
          key: "reference_style",
          label: "Reference style",
          required: true,
          blankLabel: "Select reference style",
          options: asOptions(REFERENCE_STYLES),
        },
        {
          type: "text",
          key: "reference_style_other",
          label: "Enter the reference style",
          required: (values) => values.reference_style === "Other",
          when: (values) => values.reference_style === "Other",
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          help: "Required. Supported file types: .doc, .docx",
          required: true,
          accept: ".doc,.docx",
          placeholder: "Upload your document",
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby (plagiarism_posting.rb#cost):
  //   (word_count / 100) * plagiarism_rate   (INTEGER division -> floor)
  // plagiarism_rate = PLAGIARISM_COST = 0.90 per 100 words.
  estimate: (values = {}, ctx = {}) => {
    const wc = Number(values.word_count) || 0
    const lines = []

    if (wc > 0) {
      const units = Math.floor(wc / 100)
      lines.push({
        label: `Plagiarism scan (${wc.toLocaleString()} words)`,
        amount: money(units * (ctx.rates?.plagiarism_cost ?? RATES.PLAGIARISM_COST)),
      })
    }

    const total = money(lines.reduce((sum, l) => sum + l.amount, 0))

    return { lines, total, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // plagiarism_posting = [word_count]. Null until a word count is set.
  pricePayload: (values = {}) => {
    const wc = Number(values.word_count) || 0
    if (wc <= 0) return null

    return {
      postingType: "plagiarism_posting",
      wordCount: wc,
    }
  },
}
